/**
 * Shared list/edit session state for repeatable instance orchestrators (#1154).
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { isInstanceMutationMode } from './repeatableInstancePermissionsInteraction';
import {
  applySectionReorder,
  canDeleteInstanceFromList,
  instanceIdsEqual,
  sortByDisplayOrder,
} from './repeatableSectionUtils';

import type { RepeatableInstance } from './types';
import type { RepeatableListSection } from './types';

export interface RepeatableInstanceOrchestratorConfig<
  T extends RepeatableInstance,
  TPatch,
> {
  requestId?: string;
  isEditing?: boolean;
  disabled?: boolean;
  draftInstances?: T[];
  onDraftInstancesChange?: (instances: T[]) => void;
  legacyRequestFields?: Record<string, unknown>;
  /** Key on legacyRequestFields / editedData for persisted instance arrays. */
  persistedInstancesKey: string;
  resolveForDisplay: (
    instances: T[] | undefined,
    legacySource: Record<string, unknown>,
    requestId?: string | number,
  ) => T[];
  createDraft: (data: TPatch, displayOrder: number, tempId: number) => T;
  applyPatch: (instance: T, patch: TPatch) => T;
  ensureNameForCreate: (
    data: TPatch,
    tempId: number,
    takenNames: Iterable<string | undefined>,
  ) => TPatch;
  cloneForCreate: (
    source: T,
    takenNames: Iterable<string | undefined>,
    tempId: number,
  ) => Partial<T>;
  /** Persisted Request Detail edit session — notify parent when list mutates. */
  onSessionListChange?: (instances: T[]) => void;
  /**
   * Fired at the save-new path with the unit's carried temp id
   * (`newInstanceTempIdRef.current`) BEFORE the draft is appended to the list —
   * the hook a host uses to relocate staged uploads onto `base.unit.{tempId}`
   * (#997 Story 6.2, mirroring bespoke `handleSaveRack`). The temp id passed is
   * the same id the edit body scoped the upload by (#997 Story 6.1) and the same
   * id the flush remaps temp→real on save, so the three stay joined.
   */
  onCreateInstance?: (tempId: number) => void;
  /**
   * The row id that was just saved, new or existing. Hosts pin the visible
   * chip onto that id so a later row cannot change this one.
   */
  onPersistInstance?: (instanceId: number) => void;
  /** Duplicate a child: copy that row's field-level unit chips onto the new id. */
  onDuplicateInstance?: (source: T, destTempId: number) => void;
  /**
   * Ids currently on the list. A chip edit pins these before it writes the
   * shared field unit, so changing a duplicate does not change the first child.
   */
  onListedInstances?: (instanceIds: readonly number[]) => void;
}

export function useRepeatableInstanceOrchestrator<
  T extends RepeatableInstance,
  TPatch,
>({
  requestId,
  isEditing = false,
  disabled = false,
  draftInstances = [],
  onDraftInstancesChange,
  legacyRequestFields = {},
  persistedInstancesKey,
  resolveForDisplay,
  createDraft,
  applyPatch,
  ensureNameForCreate,
  cloneForCreate,
  onSessionListChange,
  onCreateInstance,
  onPersistInstance,
  onDuplicateInstance,
  onListedInstances,
}: RepeatableInstanceOrchestratorConfig<T, TPatch>) {
  const isDraftMode = !requestId;
  const useSessionEdit = Boolean(requestId) && !isDraftMode && isEditing;
  const useLocalList = isDraftMode || useSessionEdit;
  const isInstanceEditMode = isInstanceMutationMode(isEditing, disabled);
  const listReadOnly = !useLocalList || (!isDraftMode && !isInstanceEditMode);

  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<T | null>(null);
  const [isNewInstance, setIsNewInstance] = useState(false);
  const [localSessionInstances, setLocalSessionInstances] = useState<
    T[] | null
  >(null);
  const [baselineSessionInstances, setBaselineSessionInstances] = useState<
    T[] | null
  >(null);
  const editorDirtyRef = useRef(false);
  const newInstanceTempIdRef = useRef<number | null>(null);
  const tempIdSeqRef = useRef(0);

  const allocateTempId = useCallback(() => {
    tempIdSeqRef.current += 1;
    return -(Date.now() * 1000 + tempIdSeqRef.current);
  }, []);

  const nextDisplayOrder = useCallback((items: T[]) => {
    if (items.length === 0) {
      return 1;
    }
    return Math.max(...items.map(item => item.displayOrder ?? 0)) + 1;
  }, []);

  const legacyDisplayInstances = useMemo(
    () =>
      resolveForDisplay(
        legacyRequestFields[persistedInstancesKey] as T[] | undefined,
        legacyRequestFields,
        requestId,
      ),
    [resolveForDisplay, legacyRequestFields, persistedInstancesKey, requestId],
  );

  const bootstrapSessionInstances = useCallback((): T[] => {
    return legacyDisplayInstances.map(item => ({ ...item }));
  }, [legacyDisplayInstances]);

  useEffect(() => {
    if (!useSessionEdit) {
      setLocalSessionInstances(null);
      setBaselineSessionInstances(null);
      return;
    }
    setLocalSessionInstances(prev => {
      if (prev != null) {
        return prev;
      }
      return bootstrapSessionInstances();
    });
    setBaselineSessionInstances(prev => {
      if (prev != null) {
        return prev;
      }
      return bootstrapSessionInstances().map(item => ({ ...item }));
    });
  }, [useSessionEdit, bootstrapSessionInstances]);

  const instances = isDraftMode
    ? draftInstances
    : useSessionEdit && localSessionInstances != null
    ? localSessionInstances
    : legacyDisplayInstances;

  const sortedInstances = useMemo(
    () => sortByDisplayOrder(instances),
    [instances],
  );

  useEffect(() => {
    if (!onListedInstances) return;
    const ids: number[] = [];
    for (const instance of sortedInstances) {
      if (typeof instance.id === 'number' && Number.isFinite(instance.id)) {
        ids.push(instance.id);
      }
    }
    onListedInstances(ids);
  }, [sortedInstances, onListedInstances]);

  const updateLocalList = useCallback(
    (next: T[] | ((prev: T[]) => T[])) => {
      const resolve = (prev: T[]) =>
        typeof next === 'function' ? next(prev) : next;

      if (isDraftMode) {
        onDraftInstancesChange?.(sortByDisplayOrder(resolve(draftInstances)));
        return;
      }
      if (useSessionEdit) {
        setLocalSessionInstances(prev => {
          const base = prev ?? bootstrapSessionInstances();
          setBaselineSessionInstances(baseline =>
            baseline != null ? baseline : base.map(item => ({ ...item })),
          );
          const resolved = sortByDisplayOrder(resolve(base));
          onSessionListChange?.(resolved);
          return resolved;
        });
      }
    },
    [
      draftInstances,
      isDraftMode,
      useSessionEdit,
      bootstrapSessionInstances,
      onDraftInstancesChange,
      onSessionListChange,
    ],
  );

  const handleReorderSection = useCallback(
    (section: RepeatableListSection, fromIndex: number, toIndex: number) => {
      updateLocalList(
        applySectionReorder(sortedInstances, section, fromIndex, toIndex),
      );
    },
    [sortedInstances, updateLocalList],
  );

  const handleCloseEditor = useCallback(() => {
    setIsEditorVisible(false);
    setSelectedInstance(null);
    setIsNewInstance(false);
    editorDirtyRef.current = false;
    newInstanceTempIdRef.current = null;
  }, []);

  const handleInstancePress = useCallback((instance: T) => {
    setSelectedInstance(instance);
    setIsNewInstance(false);
    setIsEditorVisible(true);
  }, []);

  const handleAddInstance = useCallback(() => {
    newInstanceTempIdRef.current = allocateTempId();
    setSelectedInstance(null);
    setIsNewInstance(true);
    setIsEditorVisible(true);
  }, [allocateTempId]);

  const handleNavigateToInstance = useCallback((instance: T) => {
    setSelectedInstance(instance);
    setIsNewInstance(false);
  }, []);

  const handleDuplicateInstance = useCallback(
    (source: T) => {
      if (!useLocalList) {
        return;
      }
      const tempId = allocateTempId();
      updateLocalList(prev => {
        const current = prev ?? sortedInstances;
        const nextOrder = nextDisplayOrder(current);
        const takenNames = current.map(i => i.instanceName);
        const duplicateData = {
          ...cloneForCreate(source, takenNames, tempId),
          displayOrder: nextOrder,
          requestId:
            typeof requestId === 'string' && requestId.length > 0
              ? parseInt(requestId, 10) ||
                (source as T & { requestId?: number }).requestId
              : (source as T & { requestId?: number }).requestId,
        } as TPatch;
        return [...current, createDraft(duplicateData, nextOrder, tempId)];
      });
      onDuplicateInstance?.(source, tempId);
    },
    [
      useLocalList,
      updateLocalList,
      sortedInstances,
      requestId,
      cloneForCreate,
      createDraft,
      allocateTempId,
      nextDisplayOrder,
      onDuplicateInstance,
    ],
  );

  const performDeleteInstance = useCallback(
    async (instance: T) => {
      if (!useLocalList || instance.id == null) {
        return;
      }
      updateLocalList(prev =>
        prev.filter(item => !instanceIdsEqual(item.id, instance.id)),
      );
    },
    [useLocalList, updateLocalList],
  );

  const handleSaveInstance = useCallback(
    async (data: TPatch) => {
      if (!useLocalList) {
        return;
      }

      if (isNewInstance) {
        const tempId = newInstanceTempIdRef.current ?? allocateTempId();
        // Relocate staged uploads onto this unit's temp scope BEFORE the draft is
        // appended, keyed by the id the edit body scoped by (#997 Story 6.2).
        onCreateInstance?.(tempId);
        onPersistInstance?.(tempId);
        const takenNames = sortedInstances.map(i => i.instanceName);
        const dataWithName = ensureNameForCreate(data, tempId, takenNames);
        const nextOrder = nextDisplayOrder(sortedInstances);
        updateLocalList([
          ...sortedInstances,
          createDraft(dataWithName, nextOrder, tempId),
        ]);
      } else if (selectedInstance?.id != null) {
        const targetId = selectedInstance.id;
        onPersistInstance?.(targetId);
        updateLocalList(prev =>
          prev.map(item =>
            instanceIdsEqual(item.id, targetId) ? applyPatch(item, data) : item,
          ),
        );
      }

      handleCloseEditor();
    },
    [
      useLocalList,
      isNewInstance,
      sortedInstances,
      selectedInstance,
      updateLocalList,
      handleCloseEditor,
      ensureNameForCreate,
      createDraft,
      applyPatch,
      allocateTempId,
      nextDisplayOrder,
      onCreateInstance,
      onPersistInstance,
    ],
  );

  const canMutate = useLocalList && isInstanceEditMode;

  const editorInitial = useMemo(() => {
    if (selectedInstance) {
      return selectedInstance;
    }
    // This draft's id is what the edit body scopes per-unit attachments by
    // (#997 Story 6.1), so for an OPEN new-unit editor it MUST be the carried
    // temp id `handleAddInstance` allocated — the same id `handleSaveInstance`
    // keys the draft, the create-time file move, and the flush's temp→real
    // remap by (#997 Story 6.2). `isNewInstance` is a real dependency: the ref
    // is written after mount, and without the recompute it forces on Add this
    // memo would keep a stale mount-time allocation, stranding pre-save
    // uploads at a key the remap never visits (Obs 6).
    const tempId =
      (isNewInstance ? newInstanceTempIdRef.current : null) ?? allocateTempId();
    return createDraft(
      { instanceStatus: 'new' } as TPatch,
      nextDisplayOrder(sortedInstances),
      tempId,
    );
  }, [
    selectedInstance,
    isNewInstance,
    sortedInstances,
    createDraft,
    allocateTempId,
    nextDisplayOrder,
  ]);

  const resetSessionInstances = useCallback(
    (instances: T[]) => {
      const snapshot = sortByDisplayOrder(instances.map(item => ({ ...item })));
      setLocalSessionInstances(snapshot);
      setBaselineSessionInstances(snapshot.map(item => ({ ...item })));
      onSessionListChange?.(snapshot);
    },
    [onSessionListChange],
  );

  return {
    isDraftMode,
    useSessionEdit,
    useLocalList,
    listReadOnly,
    isInstanceEditMode,
    canMutate,
    isEditorVisible,
    isNewInstance,
    selectedInstance,
    sortedInstances,
    baselineSessionInstances,
    editorInitial,
    editorDirtyRef,
    handleReorderSection,
    handleCloseEditor,
    handleInstancePress,
    handleAddInstance,
    handleNavigateToInstance,
    handleDuplicateInstance,
    performDeleteInstance,
    handleSaveInstance,
    resetSessionInstances,
    canDeleteInstanceFromList: (instance: T) =>
      canDeleteInstanceFromList(instance, isDraftMode),
  };
}
